import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DrawerTitleComponent,
  Icon,
  PrimengDrawerDirective,
  SpinnerFilledComponent,
  UploadComponent,
  UploadEvent,
} from '@web/shared/ui';
import { Observable, tap } from 'rxjs';
import { MultimediaChooseOrUploadDropzoneComponent } from '../../components/multimedia-choose-or-upload-dropzone/multimedia-choose-or-upload-dropzone.component';
import { Drawer } from 'primeng/drawer';
import { blank, Guid, isNil, isNotNil, Nillable } from '@web/utils';
import { MultimediaTileComponent } from '../../../shared/components/multimedia-tile/multimedia-tile.component';
import { AdminMultimediaDataSourceService } from '../../services/admin-multimedia-data-source.service';
import { AdminFetchMultimediaByIdPipe } from '../../pipes/admin-fetch-multimedia-by-id.pipe';
import { MultimediaType } from '../../../shared/enums/multimedia-type';
import { MultimediaChooseSingleComponent } from '../multimedia-choose-single/multimedia-choose-single.component';

@Component({
  selector: 'app-multimedia-choose-or-upload',
  imports: [
    CommonModule,
    UploadComponent,
    Drawer,
    PrimengDrawerDirective,
    MultimediaTileComponent,
    MultimediaChooseOrUploadDropzoneComponent,
    AdminFetchMultimediaByIdPipe,
    SpinnerFilledComponent,
    MultimediaChooseSingleComponent,
    Drawer,
    DrawerTitleComponent,
  ],
  templateUrl: './multimedia-choose-or-upload.component.html',
  styleUrl: './multimedia-choose-or-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultimediaChooseOrUploadComponent {
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(AdminMultimediaDataSourceService);

  public readonly maxFileSize = input<number>(2 * 1000 * 1000);
  public readonly allowedMimeTypes = input<string>('');
  public readonly allowedMultimediaTypes = input<Nillable<MultimediaType[]>>();
  public readonly multimediaId = input<Nillable<Guid>>();
  public readonly disabled = input(false);
  public readonly chooseClickEvent = output<Guid>();
  public readonly clearClickEvent = output();

  protected readonly shouldUpload = this.shouldUploadSignal();
  protected readonly shouldPreview = this.shouldPreviewSignal();
  protected readonly chosenId = signal<Nillable<Guid>>(null);
  protected readonly Icon = Icon;

  protected imageExplorerVisibility = false;

  private readonly uploadComponentRef = viewChild.required(UploadComponent);
  private readonly drawerComponentRef = viewChild.required(Drawer);

  public constructor() {
    effect(() => this.updateChosenByMultimedia());
  }

  public handleMultimediaLibraryClickEvent(): void {
    this.showImageExplorer();
  }

  public handleSendFileClickEvent(): void {
    this.uploadComponentRef().showChooseFileDialog();
  }

  public handleConfirmSelectedEvent(id: Guid): void {
    this.chosenId.set(id);
    this.chooseClickEvent.emit(id);

    this.hideImageExplorer();
  }

  public handleRemoveClickEvent(): void {
    this.chosenId.set(null);
    this.clearClickEvent.emit();
  }

  protected readonly uploadFn = (event: UploadEvent) => this.upload(event);

  protected shouldUploadSignal(): Signal<boolean> {
    return computed(() => isNil(this.chosenId()));
  }

  protected shouldPreviewSignal(): Signal<boolean> {
    return computed(() => isNotNil(this.chosenId()));
  }

  private updateChosenByMultimedia(): void {
    this.chosenId.set(this.multimediaId());
  }

  private upload(event: UploadEvent): Observable<void> {
    const { files } = event;
    const [file] = files;

    return this.multimediaDataSource.upload(file).pipe(
      tap((multimedia) => {
        const { id } = multimedia;

        this.chosenId.set(id);
        this.chooseClickEvent.emit(id);
      }),
      blank(),
    );
  }

  private showImageExplorer(): void {
    this.imageExplorerVisibility = true;
  }

  private hideImageExplorer(): void {
    this.drawerComponentRef().close(this.createDrawerCloseEvent());
  }

  private createDrawerCloseEvent(): Event {
    return new Event('manual-drawer-close', { cancelable: true });
  }
}
